"use client";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../shadcn/ui/collapsible";
import { ChevronDown, ChevronUp } from "lucide-react";

import { cn } from "@punchup/utils/cn";

interface CollapsibleNavGroupProps {
  label: string;
  // Optional leading icon rendered before the label (e.g. a folder glyph).
  icon?: React.ReactNode;
  // Tailwind classes applied to the trigger button so callers can match the
  // surrounding nav row style (border, padding, etc.).
  triggerClassName?: string;
  // Tailwind classes applied to the trigger's inner row, primarily its
  // vertical padding. Defaults to the drawer-row rhythm.
  triggerInnerClassName?: string;
  chevronClassName?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

/**
 * Collapsible nav group used by drawer and hamburger menus. Renders an
 * optional icon + label with a chevron-down/up toggle and a collapsible body.
 * Dropping `asChild` on the trigger lets Radix manage `aria-expanded` /
 * `aria-controls` itself.
 */
export function CollapsibleNavGroup({
  label,
  icon,
  triggerClassName,
  triggerInnerClassName = "pb-1.5 pt-3",
  chevronClassName = "w-7 h-7",
  defaultOpen,
  children,
}: CollapsibleNavGroupProps) {
  return (
    <Collapsible defaultOpen={defaultOpen} className="flex flex-col">
      <CollapsibleTrigger
        type="button"
        className={cn(
          triggerClassName,
          "[&[data-state=open]_.chevron-down]:hidden [&[data-state=closed]_.chevron-up]:hidden"
        )}
      >
        <span
          className={cn(
            "flex flex-row items-center gap-x-2 w-full",
            triggerInnerClassName
          )}
        >
          {icon}
          <span className="flex-1 text-left">{label}</span>
          <ChevronDown className={cn("chevron-down", chevronClassName)} />
          <ChevronUp className={cn("chevron-up", chevronClassName)} />
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>{children}</CollapsibleContent>
    </Collapsible>
  );
}
